<template>
  <app-custom-dialog
    v-model="showModal"
    :title="$t('choose_product_variant')"
    :width="'1000px'"
    :close-btn="true"
    @input="handleModalClose"
    @submit="addToPurchase"
  >
    <template #activator>
      <div
        class="product-selection-content"
        v-if="product && product.has_variations && product.options"
      >
        <v-container fluid>
          <v-row>
            <!-- Left Column: Product Info & Variations -->
            <v-col cols="12" lg="8">
              <!-- Product Information Card -->
              <v-card outlined class="mb-6 rounded-lg">
                <v-card-title class="text-subtitle-1 font-weight-bold">
                  <v-icon small color="primary" left>fal fa-box</v-icon>
                  {{ $t("product_information") }}
                </v-card-title>
                <v-divider class="mt-0" />

                <v-card-text class="pa-4">
                  <v-row>
                    <!-- Product Image -->
                    <v-col cols="12" sm="4" class="text-center">
                      <v-avatar tile size="100" class="product-image-container">
                        <app-img
                          :img="product.img || '/images/no_photo.jpg'"
                          width="100"
                          height="100"
                        >
                          <template v-slot:placeholder>
                            <v-row
                              class="fill-height ma-0"
                              align="center"
                              justify="center"
                            >
                              <v-icon size="48" color="grey lighten-1"
                                >fal fa-image</v-icon
                              >
                            </v-row>
                          </template>
                        </app-img>
                      </v-avatar>
                    </v-col>

                    <!-- Product Details -->
                    <v-col cols="12" sm="8">
                      <v-row dense>
                        <v-col cols="6" class="text-caption text--secondary">
                          {{ $t("product_name") }}
                        </v-col>
                        <v-col cols="6" class="text-right">
                          <span class="font-weight-medium">{{
                            product.name
                          }}</span>
                        </v-col>

                        <v-col cols="6" class="text-caption text--secondary">
                          {{ $t("barcode") }}
                        </v-col>
                        <v-col cols="6" class="text-right">
                          <span class="font-weight-medium">{{
                            product.code
                          }}</span>
                        </v-col>

                        <v-col cols="6" class="text-caption text--secondary">
                          {{ $t("category") }}
                        </v-col>
                        <v-col cols="6" class="text-right">
                          <span class="font-weight-medium">
                            {{
                              product.category?.la_name ||
                              product.category?.en_name ||
                              $t("no_category")
                            }}
                          </span>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Variations Selection with Scrollable Area -->
              <div
                v-if="product && product.has_variations && product.options"
                class="variations-section"
              >
                <v-card outlined class="variations-card rounded-lg">
                  <v-card-title
                    class="text-subtitle-1 font-weight-bold variations-header"
                  >
                    <v-icon small color="primary" left>fal fa-list</v-icon>
                    {{ $t("select_options") }}
                  </v-card-title>

                  <!-- Scrollable Variations Content -->
                  <div class="variations-content">
                    <v-card-text class="py-0">
                      <ProductVariationDisplay
                        :product="{
                          ...product,
                          product_options: product.options,
                          variants: product.variants,
                          track_stock: 0, // Disable stock tracking for purchase
                        }"
                        :selected-options="selectedVariationOptions"
                        :cart-items="purchaseItems"
                        @option-selected="handleOptionSelected"
                      />
                    </v-card-text>
                  </div>
                </v-card>
              </div>
            </v-col>

            <!-- Right Column: Purchase Details -->
            <v-col cols="12" lg="4">
              <div class="sticky-sidebar">
                <!-- Purchase Details Card -->
                <v-card outlined class="mb-6 rounded-lg">
                  <v-card-title class="text-subtitle-1 font-weight-bold">
                    <v-icon small color="primary" left
                      >fal fa-shopping-cart</v-icon
                    >
                    {{ $t("purchase_details") }}
                  </v-card-title>
                  <v-divider class="mt-0" />

                  <v-card-text class="pa-4">
                    <!-- Quantity Selection -->
                    <div class="quantity-section mb-4">
                      <label class="text-caption grey--text mb-2 d-block">
                        {{ $t("quantity") }}
                      </label>
                      <div class="quantity-controls">
                        <v-btn
                          fab
                          small
                          outlined
                          @click="decrementQuantity"
                          :disabled="quantity <= 1"
                          class="quantity-btn"
                        >
                          <v-icon>fal fa-minus</v-icon>
                        </v-btn>

                        <v-text-field
                          v-model.number="quantity"
                          type="number"
                          min="1"
                          outlined
                          dense
                          class="quantity-input mx-2"
                          hide-details
                        ></v-text-field>

                        <v-btn
                          fab
                          small
                          outlined
                          @click="incrementQuantity"
                          class="quantity-btn"
                        >
                          <v-icon>fal fa-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <!-- Price Buy -->
                    <div class="price-section mb-4">
                      <label class="text-caption grey--text mb-2 d-block">
                        {{ $t("price_buy") }}
                      </label>
                      <v-text-field
                        v-model.number="priceBuy"
                        type="number"
                        min="0"
                        step="0.01"
                        outlined
                        dense
                        :suffix="
                          selectedVariant?.currency?.code ||
                          product.currency?.code
                        "
                        hide-details
                      ></v-text-field>
                    </div>

                    <!-- Price Sale (Calculated) -->
                    <div class="price-section mb-4">
                      <label class="text-caption grey--text mb-2 d-block">
                        {{ $t("price_sale") }}
                      </label>
                      <v-text-field
                        v-model.number="priceSale"
                        type="number"
                        min="0"
                        step="0.01"
                        outlined
                        dense
                        :suffix="
                          selectedVariant?.currency?.code ||
                          product.currency?.code
                        "
                        hide-details
                        readonly
                        class="price-sale-input"
                      ></v-text-field>
                    </div>

                    <!-- Expired Date -->
                    <div class="date-section mb-4">
                      <label class="text-caption grey--text mb-2 d-block">
                        {{ $t("expired_date") }}
                      </label>
                      <v-text-field
                        v-model="expiredDate"
                        type="date"
                        outlined
                        dense
                        hide-details
                      ></v-text-field>
                    </div>

                    <!-- Supplier Selection -->
                    <div class="supplier-section mb-4" v-if="suppliers.length">
                      <label class="text-caption grey--text mb-2 d-block">
                        {{ $t("supplier") }}
                      </label>
                      <v-select
                        v-model="selectedSupplier"
                        :items="suppliers"
                        item-text="name"
                        item-value="id"
                        outlined
                        dense
                        hide-details
                        :placeholder="$t('select_supplier')"
                      ></v-select>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </template>
  </app-custom-dialog>
</template>

<script>
import ProductVariationDisplay from "./ProductVariationDisplay.vue";
import { toast } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";

export default {
  name: "PurchaseVariantModal",
  components: {
    ProductVariationDisplay,
  },
  mixins: [myMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    suppliers: {
      type: Array,
      default: () => [],
    },
    purchaseItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      quantity: 1,
      priceBuy: 0,
      priceSale: 0,
      expiredDate: this.product?.expired_date || null,
      selectedSupplier: null,
      selectedVariationOptions: {},
      loading: false,
    };
  },
  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
    selectedVariant() {
      if (
        !this.product ||
        !this.product.has_variations ||
        !this.product.options ||
        !this.product.variants ||
        !this.product.variants.length
      ) {
        return null;
      }
      // Find the variant that matches all selected options
      const selected = this.selectedVariationOptions;
      return this.product.variants.find((variant) => {
        if (!variant.options) return false;
        return Object.entries(selected).every(
          ([key, value]) => variant.options[key] === value
        );
      });
    },
    totalCost() {
      const priceBuy = parseFloat(this.priceBuy) || 0;
      const quantity = parseInt(this.quantity) || 0;
      return priceBuy * quantity;
    },
    canAddToPurchase() {
      if (!this.product || !this.product.id) return false;

      const quantity = parseInt(this.quantity) || 0;
      const priceBuy = parseFloat(this.priceBuy) || 0;

      if (this.product.has_variations) {
        // Require all options to be selected and a valid variant to be found
        const requiredOptions = this.product.options
          ? this.product.options.length
          : 0;
        const selectedCount = Object.keys(this.selectedVariationOptions).length;

        return (
          selectedCount === requiredOptions &&
          this.selectedVariant &&
          quantity > 0 &&
          priceBuy > 0
        );
      }

      // For non-variant products
      return quantity > 0 && priceBuy > 0;
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
    product: {
      handler() {
        if (this.product && this.product.vendor_id) {
          this.selectedSupplier = this.product.vendor_id;
        }
      },
      immediate: true,
    },
    selectedVariationOptions: {
      handler() {
        this.updatePrices();
      },
      deep: true,
    },
    priceBuy() {
      this.calculatePriceSale();
    },
  },
  methods: {
    handleModalClose(value) {
      this.showModal = value;
      if (!value) {
        this.closeModal();
      }
    },
    handleOptionSelected(data) {
      this.selectedVariationOptions = data.allSelectedOptions;
    },
    incrementQuantity() {
      this.quantity++;
    },
    decrementQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },
    calculatePriceSale() {
      const priceBuy = parseFloat(this.priceBuy) || 0;
      this.priceSale = priceBuy.toFixed(2);
    },
    updatePrices() {
      if (this.selectedVariant) {
        // Set default prices from selected variant
        this.priceBuy = parseFloat(this.selectedVariant.cost) || 0;
        this.priceSale = parseFloat(this.selectedVariant.price) || 0;
      } else if (!this.product.has_variations) {
        // For non-variant products
        this.priceBuy = parseFloat(this.product.price_buy) || 0;
        this.priceSale = parseFloat(this.product.price_sale) || 0;
      }
    },
    resetForm() {
      this.quantity = 1;
      this.selectedVariationOptions = {};
      this.updatePrices();
      // Set default supplier from product vendor
      this.selectedSupplier = this.product?.vendor_id || null;
      // Reset expired_date to product's original value or null
      this.expiredDate = this.product?.expired_date || null;
    },
    closeModal() {
      this.showModal = false;
    },
    addToPurchase() {
      if (!this.canAddToPurchase) {
        toast(this, "please_fill_all_required_fields", "error");
        return;
      }

      try {
        this.loading = true;

        // Prepare purchase data
        const purchaseData = {
          product: this.product,
          variant: this.selectedVariant,
          quantity: this.quantity,
          price_buy: this.priceBuy,
          price_sale: this.priceSale,
          expired_date: this.expiredDate,
          vendor_id: this.selectedSupplier,
          selectedVariationOptions: this.product.has_variations
            ? this.selectedVariationOptions
            : null,
        };

        // Emit the purchase data to parent component
        this.$emit("add-to-purchase", purchaseData);

        // Close modal
        this.showModal = false;
      } catch (error) {
        console.error("Error adding to purchase:", error);
        toast(this, "error_adding_variant", "error");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.product-selection-content {
  max-height: 85vh;
  overflow-y: auto;
}

.product-image-container {
  border: 1px solid #e0e0e0;
}

.variations-section {
  margin-bottom: 20px;
}

.variations-card {
  border-radius: 8px;
  overflow: hidden;
}

.variations-header {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 20px;
}

.variations-content {
  max-height: 400px;
  overflow-y: auto;
}

.quantity-controls {
  display: flex;
  align-items: center;
  justify-content: center;
}

.quantity-btn {
  border: 1px solid #e0e0e0;
}

.quantity-input {
  max-width: 100px;
}

.price-sale-input {
  background-color: #f5f5f5;
}

.sticky-sidebar {
  position: sticky;
  top: 0px;
}

@media (max-width: 1200px) {
  .product-selection-content {
    max-height: 80vh;
  }

  .variations-content {
    max-height: 350px;
  }
}

@media (max-width: 960px) {
  .sticky-sidebar {
    position: static;
  }

  .product-selection-content {
    max-height: 75vh;
  }

  .variations-content {
    max-height: 300px;
  }
}

@media (max-width: 600px) {
  .product-selection-content {
    max-height: 70vh;
  }

  .variations-content {
    max-height: 250px;
  }

  .quantity-controls {
    flex-direction: column;
    gap: 16px;
  }

  .variations-header {
    padding: 12px 16px;
  }
}

.variations-content::-webkit-scrollbar {
  width: 6px;
}

.variations-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.variations-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.variations-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
